import { Reducer, useCallback, useReducer } from 'react';

import { ApiStatus, API_STATUS } from '@/constants';

import {
  guestSurviveApi,
  GuestSurviveApiResponse,
  isGuestSurviveApiResultCode,
  GUEST_SURVIVE_API_RESULT_CODE,
  GuestSurviveApiRequest,
} from '../guestSurviveApi';

/**
 * ゲスト生存受信API呼び出しエラータイプ
 */
export const GUEST_SURVIVE_ERROR_TYPE = {
  NONE: 'NONE', // エラーなし
  API_ERROR: 'API_ERROR', // APIレスポンスエラー
  REQUEST_ERROR: 'REQUEST_ERROR', // リクエストエラー(オフラインなど)
} as const;
export type GuestSurviveErrorType =
  (typeof GUEST_SURVIVE_ERROR_TYPE)[keyof typeof GUEST_SURVIVE_ERROR_TYPE];

/**
 * State
 */
export type guestSurviveState = {
  // レスポンスデータ
  data?: GuestSurviveApiResponse;
  // APIの呼び出し状態
  status: ApiStatus;
  // エラータイプ
  errorType: GuestSurviveErrorType;
};

/**
 * Actionタイプ
 */
const GUEST_SURVIVE_ACTION_TYPE = {
  SET_GUEST_SURVIVE: 'SET_GUEST_SURVIVE', // API呼び出し開始
  SET_GUEST_SURVIVE_SUCCESS: 'SET_GUEST_SURVIVE_SUCCESS', // API呼び出し成功
  SET_GUEST_SURVIVE_FAILED: 'SET_GUEST_SURVIVE_FAILED', // API呼び出し成功したが結果がOK以外
  SET_GUEST_SURVIVE_REQUEST_ERROR: 'SET_GUEST_SURVIVE_REQUEST_ERROR', // リクエスト中の何らかのエラー(オフライン時含む)
} as const;

/**
 * Action
 */
type guestSurviveAction = {
  data?: GuestSurviveApiResponse;
  type: keyof typeof GUEST_SURVIVE_ACTION_TYPE;
  errorType?: GuestSurviveErrorType;
};

/**
 * reducer関数
 *
 * @param state
 * @param action
 * @returns
 */
const reducer: Reducer<guestSurviveState, guestSurviveAction> = (
  state: guestSurviveState,
  action: guestSurviveAction,
) => {
  switch (action.type) {
    case GUEST_SURVIVE_ACTION_TYPE.SET_GUEST_SURVIVE:
      return {
        ...state,
        status: API_STATUS.LOADING,
        errorType: GUEST_SURVIVE_ERROR_TYPE.NONE,
      };

    case GUEST_SURVIVE_ACTION_TYPE.SET_GUEST_SURVIVE_SUCCESS:
      return {
        ...state,
        data: action.data,
        status: API_STATUS.SUCCESS,
        errorType: GUEST_SURVIVE_ERROR_TYPE.NONE,
      };

    case GUEST_SURVIVE_ACTION_TYPE.SET_GUEST_SURVIVE_FAILED:
      return {
        ...state,
        data: action.data,
        status: API_STATUS.FAILED,
        errorType: GUEST_SURVIVE_ERROR_TYPE.API_ERROR,
      };

    case GUEST_SURVIVE_ACTION_TYPE.SET_GUEST_SURVIVE_REQUEST_ERROR:
      return {
        ...state,
        status: API_STATUS.FAILED,
        errorType: GUEST_SURVIVE_ERROR_TYPE.REQUEST_ERROR,
      };
    default:
      return state;
  }
};

/**
 * ゲスト生存受信API 呼び出しhooks
 *
 * @returns
 */
export const useGuestSurviveApi = () => {
  const [state, dispatch] = useReducer(reducer, {
    data: undefined,
    status: API_STATUS.IDLE,
    errorType: GUEST_SURVIVE_ERROR_TYPE.NONE,
  });

  /**
   * API呼び出し
   */
  const surviveGuest = useCallback(
    (request: GuestSurviveApiRequest) => {
      if (state.status === API_STATUS.LOADING) {
        return;
      }

      dispatch({
        type: GUEST_SURVIVE_ACTION_TYPE.SET_GUEST_SURVIVE,
      });

      guestSurviveApi(request)
        .then((response: GuestSurviveApiResponse) => {
          // 成功
          if (response.resultCode === GUEST_SURVIVE_API_RESULT_CODE.OK) {
            dispatch({
              type: GUEST_SURVIVE_ACTION_TYPE.SET_GUEST_SURVIVE_SUCCESS,
              data: response,
            });

            return;
          }

          // 意図しない結果コードの型だった場合は失敗とする
          if (!isGuestSurviveApiResultCode(response.resultCode)) {
            dispatch({
              type: GUEST_SURVIVE_ACTION_TYPE.SET_GUEST_SURVIVE_FAILED,
              data: {
                ...response,
                resultCode: GUEST_SURVIVE_API_RESULT_CODE.ERR_UNKNOWN,
              } as GuestSurviveApiResponse,
            });

            return;
          }

          // 失敗
          dispatch({
            type: GUEST_SURVIVE_ACTION_TYPE.SET_GUEST_SURVIVE_FAILED,
            data: response,
          });
        })
        .catch((_) => {
          dispatch({
            type: GUEST_SURVIVE_ACTION_TYPE.SET_GUEST_SURVIVE_REQUEST_ERROR,
          });
        });
    },
    [state.status],
  );

  return {
    guestSurviveState: state,
    surviveGuest,
  };
};
